const API_URL = (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";

let token: string | null = localStorage.getItem("cortex_token");

export function setToken(t: string) {
  token = t;
  localStorage.setItem("cortex_token", t);
}

export function getToken() {
  return token;
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || "Request failed");
  }
  return res.json();
}

export const api = {
  auth: (initData: string) =>
    request<{ token: string; user: any }>("/auth/telegram", {
      method: "POST",
      body: JSON.stringify({ initData }),
    }),

  getNotes: (params?: { limit?: number; offset?: number; q?: string; project_id?: string }) => {
    const sp = new URLSearchParams();
    if (params?.limit) sp.set("limit", String(params.limit));
    if (params?.offset) sp.set("offset", String(params.offset));
    if (params?.q) sp.set("q", params.q);
    if (params?.project_id) sp.set("project_id", params.project_id);
    return request<{ notes: Note[] }>(`/notes?${sp}`);
  },

  getNote: (id: string) => request<{ note: Note }>(`/notes/${id}`),

  createNote: (data: { content: string; type?: string; project_id?: string }) =>
    request<{ note: Note }>("/notes", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  createVoiceNote: async (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<{ note: Note; transcript: string }>("/notes/voice", {
      method: "POST",
      body: form,
    });
  },

  createPhotoNote: (imageBase64: string, mimeType?: string) =>
    request<{ note: Note; ocr_text: string }>("/notes/photo", {
      method: "POST",
      body: JSON.stringify({ image_base64: imageBase64, mime_type: mimeType }),
    }),

  query: (query: string) =>
    request<{ answer: string; sources: string[] }>("/notes/query", {
      method: "POST",
      body: JSON.stringify({ query }),
    }),

  deleteNote: (id: string) =>
    request<{ ok: boolean }>(`/notes/${id}`, { method: "DELETE" }),

  getProjects: () => request<{ projects: Project[] }>("/projects"),
  createProject: (data: { name: string; color?: string }) =>
    request<{ project: Project }>("/projects", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  deleteProject: (id: string) =>
    request<{ ok: boolean }>(`/projects/${id}`, { method: "DELETE" }),
  getProjectNotes: (id: string) =>
    request<{ notes: Note[] }>(`/projects/${id}/notes`),

  getLatestDigest: (type: "daily" | "weekly" = "daily") =>
    request<{ digest: Digest | null }>(`/digests/latest?type=${type}`),
  generateDigest: (type: "daily" | "weekly" = "daily") =>
    request<{ digest: Digest }>("/digests/generate", {
      method: "POST",
      body: JSON.stringify({ type }),
    }),

  createInvoice: (plan: "monthly" | "yearly") =>
    request<{ invoice_url: string; plan: string; stars: number }>(
      "/payments/create",
      { method: "POST", body: JSON.stringify({ plan }) }
    ),
};

export interface Note {
  id: string;
  content: string;
  summary: string | null;
  type: string;
  is_task: boolean;
  task_text: string | null;
  task_due_at: string | null;
  task_status: string | null;
  project_id: string | null;
  created_at: string;
  source_file_url?: string | null;
}

export interface Project {
  id: string;
  name: string;
  color: string;
  icon: string;
  created_at: string;
}

export interface Digest {
  id: string;
  type: string;
  content: {
    main: string[];
    tasks: string[];
    ideas: string[];
    connections: string[];
  };
  period_start: string;
  period_end: string;
  created_at: string;
}
